// src/services/projectProgressService.js
/**
 * Project Progress Monitoring Service
 * 
 * Handles API calls for Progress Monitoring module
 * Views aggregated progress across projects, segments/sites, and milestones
 * 
 * IMPORTANT: This is SEPARATE from progressService.js
 * - progressService.js      → Field submission approval workflow
 * - projectProgressService.js → Project progress monitoring (this file)
 * 
 * Backend: ProjectController.java, ProjectProgressService.java
 * Endpoints:
 * - GET /api/projects/progress          → All projects with progress summary
 * - GET /api/projects/{id}/progress     → Single project with complete hierarchy
 */

import api from '../config/axios';

const BASE_PATH = '/projects';

// ============= PROJECT PROGRESS MONITORING ENDPOINTS =============

/**
 * Get all projects with progress summary
 * GET /api/projects/progress
 * 
 * Returns: Array of projects with progress stats
 * - Overall progress percentage
 * - Milestone statistics
 * - RAG status indicators
 * - Timeline status
 * - Budget summary
 * 
 * Does NOT include: Milestone tree (lightweight for list view)
 * 
 * @returns {Promise<Array>} Array of ProjectProgressResponse (summary)
 */
export const getAllProjectsProgress = async () => {
  const response = await api.get(`${BASE_PATH}/progress`);
  return response.data;
};

/**
 * Get single project with complete progress hierarchy
 * GET /api/projects/{id}/progress
 * 
 * Returns: Project with segments/sites and milestone tree
 * - All data from getAllProjectsProgress
 * - FO Segments array with milestones (if FO project)
 * - Tower Sites array with milestones (if TOWER project)
 * - Complete milestone tree (hierarchical)
 * 
 * Used by: Progress detail/drilldown view
 * 
 * @param {number} projectId - Project ID
 * @returns {Promise<Object>} ProjectProgressResponse (detailed with tree)
 */
export const getProjectProgressById = async (projectId) => {
  const response = await api.get(`${BASE_PATH}/${projectId}/progress`);
  return response.data;
};

// ============= HELPER FUNCTIONS =============

/**
 * Get progress status badge class
 * Based on progressStatus field
 */
export const getProgressStatusBadgeClass = (status) => {
  const statusMap = {
    'ON_TRACK': 'bg-green-100 text-green-800',
    'AT_RISK': 'bg-yellow-100 text-yellow-800',
    'DELAYED': 'bg-red-100 text-red-800'
  };
  return statusMap[status] || 'bg-gray-100 text-gray-800';
};

/**
 * Get progress status display name
 */
export const getProgressStatusDisplayName = (status) => {
  const statusMap = {
    'ON_TRACK': 'On Track',
    'AT_RISK': 'At Risk',
    'DELAYED': 'Delayed'
  };
  return statusMap[status] || status;
};

/**
 * Get RAG status badge class
 * Red, Amber, Green status
 */
export const getRAGStatusBadgeClass = (ragStatus) => {
  const statusMap = {
    'RED': 'bg-red-100 text-red-800 border border-red-300',
    'AMBER': 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    'GREEN': 'bg-green-100 text-green-800 border border-green-300'
  };
  return statusMap[ragStatus] || 'bg-gray-100 text-gray-800';
};

/**
 * Get RAG status icon
 */
export const getRAGStatusIcon = (ragStatus) => {
  const iconMap = {
    'RED': '🔴',
    'AMBER': '🟡',
    'GREEN': '🟢'
  };
  return iconMap[ragStatus] || '⚪';
};

/**
 * Get project status badge class
 * Same as projectService but duplicated here for convenience
 */
export const getProjectStatusBadgeClass = (status) => {
  const statusMap = {
    'STARTED': 'bg-blue-100 text-blue-800',
    'IN_PROGRESS': 'bg-yellow-100 text-yellow-800',
    'ON_HOLD': 'bg-orange-100 text-orange-800',
    'COMPLETED': 'bg-green-100 text-green-800',
    'CLOSED': 'bg-purple-100 text-purple-800',
    'DROP': 'bg-red-100 text-red-800'
  };
  return statusMap[status] || 'bg-gray-100 text-gray-800';
};

/**
 * Get project status display name
 */
export const getProjectStatusDisplayName = (status) => {
  const statusMap = {
    'STARTED': 'Started',
    'IN_PROGRESS': 'In Progress',
    'ON_HOLD': 'On Hold',
    'COMPLETED': 'Completed',
    'CLOSED': 'Closed',
    'DROP': 'Dropped'
  };
  return statusMap[status] || status;
};

/**
 * Get project type badge class
 */
export const getProjectTypeBadgeClass = (type) => {
  const typeMap = {
    'TOWER': 'bg-purple-100 text-purple-800',
    'FO': 'bg-green-100 text-green-800',
    'TOWER_COLO': 'bg-indigo-100 text-indigo-800'
  };
  return typeMap[type] || 'bg-gray-100 text-gray-800';
};

/**
 * Get milestone status badge class
 */
export const getMilestoneStatusBadgeClass = (status) => {
  const statusMap = {
    'NOT_STARTED': 'bg-gray-100 text-gray-800',
    'IN_PROGRESS': 'bg-blue-100 text-blue-800',
    'COMPLETED': 'bg-green-100 text-green-800',
    'ON_HOLD': 'bg-orange-100 text-orange-800'
  };
  return statusMap[status] || 'bg-gray-100 text-gray-800';
};

/**
 * Get milestone status display name
 */
export const getMilestoneStatusDisplayName = (status) => {
  const statusMap = {
    'NOT_STARTED': 'Not Started',
    'IN_PROGRESS': 'In Progress',
    'COMPLETED': 'Completed',
    'ON_HOLD': 'On Hold'
  };
  return statusMap[status] || status;
};

/**
 * Format progress percentage
 * @param {number} progress - Progress value (0-100)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage (e.g., "65.5%")
 */
export const formatProgress = (progress, decimals = 1) => {
  if (progress === null || progress === undefined) {
    return '0.0%';
  }
  return `${progress.toFixed(decimals)}%`;
};

/**
 * Format currency (Indonesian Rupiah)
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency (e.g., "Rp 1.500.000")
 */
export const formatCurrency = (amount) => {
  if (!amount) return 'Rp 0';
  
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Format days remaining
 * @param {number} days - Number of days
 * @returns {string} Formatted text (e.g., "45 days" or "OVERDUE")
 */
export const formatDaysRemaining = (days) => {
  if (days === null || days === undefined) {
    return '-';
  }
  
  if (days < 0) {
    return `OVERDUE (${Math.abs(days)} days)`;
  }
  
  if (days === 0) {
    return 'Today';
  }
  
  if (days === 1) {
    return '1 day';
  }
  
  return `${days} days`;
};

/**
 * Calculate progress bar color based on variance
 * @param {number} variance - Progress variance (actual - expected)
 * @returns {string} Tailwind color class
 */
export const getProgressBarColor = (variance) => {
  if (variance === null || variance === undefined) {
    return 'bg-blue-600';
  }
  
  if (variance < -15) {
    return 'bg-red-600'; // Severely delayed
  } else if (variance < -5) {
    return 'bg-yellow-500'; // At risk
  } else if (variance >= 0) {
    return 'bg-green-600'; // On track or ahead
  } else {
    return 'bg-blue-600'; // Slightly behind but acceptable
  }
};

/**
 * Get timeline status text
 * @param {number} daysRemaining - Days remaining until deadline
 * @param {boolean} isOverdue - Is project overdue
 * @returns {string} Status text with emoji
 */
export const getTimelineStatusText = (daysRemaining, isOverdue) => {
  if (isOverdue) {
    return '🔴 Overdue';
  }
  
  if (daysRemaining === null || daysRemaining === undefined) {
    return '⚪ No deadline';
  }
  
  if (daysRemaining < 7) {
    return '🟡 Critical';
  } else if (daysRemaining < 30) {
    return '🟡 Urgent';
  } else {
    return '🟢 On time';
  }
};

/**
 * Sort projects by different criteria
 * @param {Array} projects - Array of projects
 * @param {string} sortBy - Sort field (progress, variance, deadline, rag)
 * @param {string} direction - Sort direction (asc, desc)
 * @returns {Array} Sorted projects
 */
export const sortProjects = (projects, sortBy, direction = 'desc') => {
  const sorted = [...projects].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'progress':
        aValue = a.totalProgress || 0;
        bValue = b.totalProgress || 0;
        break;
      case 'variance':
        aValue = a.progressVariance || 0;
        bValue = b.progressVariance || 0;
        break;
      case 'deadline':
        aValue = a.daysRemaining || 0;
        bValue = b.daysRemaining || 0;
        break;
      case 'rag':
        // RED = 0, AMBER = 1, GREEN = 2 (so RED sorts first)
        const ragOrder = { 'RED': 0, 'AMBER': 1, 'GREEN': 2 };
        aValue = ragOrder[a.overallRAGStatus] || 3;
        bValue = ragOrder[b.overallRAGStatus] || 3;
        break;
      case 'name':
        aValue = a.projectName || '';
        bValue = b.projectName || '';
        return direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      default:
        return 0;
    }
    
    if (direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
  
  return sorted;
};

/**
 * Filter projects by RAG status
 * @param {Array} projects - Array of projects
 * @param {string} ragFilter - RAG status to filter (RED, AMBER, GREEN, ALL)
 * @returns {Array} Filtered projects
 */
export const filterProjectsByRAG = (projects, ragFilter) => {
  if (ragFilter === 'ALL' || !ragFilter) {
    return projects;
  }
  
  return projects.filter(project => project.overallRAGStatus === ragFilter);
};

/**
 * Filter projects by type
 * @param {Array} projects - Array of projects
 * @param {string} typeFilter - Project type to filter (TOWER, FO, ALL)
 * @returns {Array} Filtered projects
 */
export const filterProjectsByType = (projects, typeFilter) => {
  if (typeFilter === 'ALL' || !typeFilter) {
    return projects;
  }
  
  return projects.filter(project => project.projectType === typeFilter);
};

/**
 * Search projects by keyword
 * @param {Array} projects - Array of projects
 * @param {string} keyword - Search keyword
 * @returns {Array} Filtered projects
 */
export const searchProjects = (projects, keyword) => {
  if (!keyword || keyword.trim() === '') {
    return projects;
  }
  
  const lowerKeyword = keyword.toLowerCase();
  
  return projects.filter(project => 
    project.projectName?.toLowerCase().includes(lowerKeyword) ||
    project.projectCode?.toLowerCase().includes(lowerKeyword) ||
    project.customerName?.toLowerCase().includes(lowerKeyword)
  );
};

// ============= EXPORT DEFAULT =============

export default {
  // API calls
  getAllProjectsProgress,
  getProjectProgressById,
  
  // Badge helpers
  getProgressStatusBadgeClass,
  getProgressStatusDisplayName,
  getRAGStatusBadgeClass,
  getRAGStatusIcon,
  getProjectStatusBadgeClass,
  getProjectStatusDisplayName,
  getProjectTypeBadgeClass,
  getMilestoneStatusBadgeClass,
  getMilestoneStatusDisplayName,
  
  // Formatters
  formatProgress,
  formatCurrency,
  formatDaysRemaining,
  getProgressBarColor,
  getTimelineStatusText,
  
  // Filters & sorting
  sortProjects,
  filterProjectsByRAG,
  filterProjectsByType,
  searchProjects
};